// TagsList component for displaying and managing the soft tags table
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import routes from '@/app/routes';
import type { AppDispatch } from '../../../app/store';
import { Button, Title, DropdownMenu } from '@yaradigitallabs/ahua-react';

import BulkImportModal from './bulkImport/BulkImportModal';
import BulkImportAlert from './bulkImport/BulkImportAlert';
import {
  bulkImportMenuItems,
  fileUploadResult,
} from './bulkImport/bulkImportConstants';
import { createColumns } from './TagsColumns';
import { Filters } from './TagsFilters';
import Pagination from './TagsPagination';
import { TagsTable } from './TagsTable';

import {
  selectSearchFilters,
  selectTags,
  selectUploadBulkImportErrorCsv,
  selectTagsLoading,
} from '../store/tags.selectors';
import { clearSoftTag } from '../store/tags.slice';
import { getSoftTags } from '../store/tags.thunk';
import type { Tags } from '../types/tags.types';
import { paginationValues } from '../utils/constants';

type UploadResult = 'Upload failed' | 'Upload successful' | null;

// Main component for soft tags table and filter/search UI
export const TagsList = () => {
  // Local state for table data, sorting, row selection, and filter status
  const [tagsData, setTagsData] = useState<Tags[]>([]); // Table data
  const [hasActiveFilter, setHasActiveFilter] = useState<boolean>(false); // Tracks if any filter is active
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean | null>(null);

  const { newSoftTags } = useAppSelector(state => state.createSoftTag);

  // Redux hooks for dispatching actions and selecting state
  const dispatch = useDispatch<AppDispatch>();
  const tags = useAppSelector(selectTags); // List of tags from store
  const loading = useAppSelector(selectTagsLoading); // Loading state
  const uploadBulkImportErrorCsv = useAppSelector(
    selectUploadBulkImportErrorCsv
  );
  const pagination = useAppSelector(state => state.tags.pagination); // Pagination info
  const searchFilters = useAppSelector(selectSearchFilters); // Current search filters

  // Initial fetch of soft tags on mount
  useEffect(() => {
    dispatch(
      getSoftTags({ size: 10, page: 1, sort_by: 'name', sort_order: 'asc' })
    );
  }, [dispatch]);

  // Update local table data when tags change
  useEffect(() => {
    setTagsData(tags);
  }, [tags]);

  // Handle pagination changes (page/size)
  const handlePaginationChange = (paginationData: {
    page: number;
    size: number;
  }) => {
    // Prepare payload with current filters and new pagination
    const payload = {
      page: paginationData.page,
      size: paginationData.size,
      area: searchFilters.area,
      search_term: searchFilters.search_term,
      site: searchFilters.site,
      production_unit: searchFilters.production_unit,
    };
    dispatch(getSoftTags(payload));
  };

  const navigate = useNavigate();

  // Handle dropdown menu actions
  const handleMenuAction = (action: string, data: Tags) => {
    switch (action) {
      case 'edit':
        // Handle edit action - you can navigate to edit page or open modal
        navigate(`${routes.EDIT_SOFT_TAG}`, { state: { softTagData: data } });
        break;
      case 'backfill':
        navigate(`${routes.BACKFILL_SOFT_TAG}`, {
          state: { softTagData: data },
        });
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Create columns with menu action handler
  const columnsWithActions = createColumns(handleMenuAction);

  const handleCreateNewSoftTag = () => {
    dispatch(clearSoftTag());
    navigate(routes.CREATE_SOFT_TAG);
  };

  const handleBulkUploadResult = (result: UploadResult) => {
    setUploadSuccess(result === fileUploadResult.UPLOAD_SUCCESS);
  };

  return (
    <div className="flex flex-col w-full">
      {/* Header and create button */}
      <div className="flex flex-wrap justify-between items-start px-4 py-2">
        <div className="self-center">
          {/* Table title and description */}
          <Title size="n">Soft tags database</Title>
        </div>
        {/* Button to create a new soft tag */}
        <div className="flex gap-3 justify-between items-center">
          <Button
            onClick={handleCreateNewSoftTag}
            size="s"
            colorConcept="brand"
            variant="primary"
          >
            Create Soft Tag
          </Button>
          <DropdownMenu dir="ltr">
            <DropdownMenu.TriggerButton title="Click to open the dropdown menu" />
            <DropdownMenu.Content className="mt-4 mr-4">
              <DropdownMenu.Item
                icon="AddBox"
                onClick={() => {
                  setShowImportModal(true);
                }}
              >
                {bulkImportMenuItems.CREATE_SOFT_TAGS}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                icon="BulletedList"
                onClick={() => {
                  navigate(routes.BULK_IMPORTS_HISTORY);
                }}
              >
                {bulkImportMenuItems.BULK_IMPORTS_HISTORY}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      </div>
      {showImportModal && (
        <BulkImportModal
          open={showImportModal}
          onOpenChange={setShowImportModal}
          onBulkImportUpload={handleBulkUploadResult}
        />
      )}
      {newSoftTags?.length > 0 && (
        <div className="mt-2 mb-2">
          <Title size="xs" className="mb-2 px-4">
            Recently saved soft tags
          </Title>
          <TagsTable
            hasActiveFilter={false}
            columns={columnsWithActions}
            tags={newSoftTags}
            loading={false}
          />
        </div>
      )}
      {/* Filters bar for searching and filtering tags */}
      <Filters onActiveFiltersChange={setHasActiveFilter} />

      <TagsTable
        hasActiveFilter={hasActiveFilter}
        columns={columnsWithActions}
        tags={tagsData}
        loading={loading}
      />
      {/* Pagination controls for navigating pages */}
      <Pagination
        limitOptions={paginationValues}
        loading={loading}
        dataCount={pagination.count}
        paginatedData={{ size: pagination.size, page: pagination.page }}
        setPaginatedData={handlePaginationChange}
      />
      {uploadSuccess !== null && (
        <BulkImportAlert
          open={true}
          onOpenChange={() => setUploadSuccess(null)}
          error={uploadSuccess === false}
          csvContent={uploadBulkImportErrorCsv}
        />
      )}
    </div>
  );
};
